import i18n from "@/i18n";
import { ROUTE_CONFIG } from "@/routes/config";

import { PermissionRoles } from "@/enum";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { renderSidebarIcon, SidebarIcons } from "./sidebarIcons";

export interface SidenavItem {
	/** Stable key for expandable groups (localStorage expansion persistence). */
	id?: string;
	title: string;
	path?: string;
	/** Sub-items; when set, the item renders as an expandable group and `path` is ignored. */
	subItems?: SidenavItem[];
	onClick?: () => void;
	isBeta?: boolean;
	strictPath?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	icon?: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	iconActive?: any;
	type?: string;
	permission?: PermissionRoles;
	companyIds?: number[];
	feature?: FeaturesCodes;
	notFeature?: FeaturesCodes;
	isLoading?: boolean;
	explanationText?: string;
	dataOnboarding?: string;
}

interface NavItemInner {
	dashboard: SidenavItem[];
	messages: SidenavItem[];
	signalsLeads: SidenavItem[];
	configurations: SidenavItem[];
	help: SidenavItem[];
	lower: SidenavItem[];
}

interface NavItemConfig {
	ALL: NavItemInner;
}

export const useNavigationGetter = (): NavItemConfig => {
	return {
		ALL: {
			dashboard: [
				{
					title: i18n.t("sidebar.dashboard"),
					path: ROUTE_CONFIG.DASHBOARD,
					icon: renderSidebarIcon(SidebarIcons.dashboard),
					type: "icon",
				},
			],
			messages: [
				{
					title: i18n.t("sidebar.compose"),
					path: ROUTE_CONFIG.MESSAGE_CENTER_COMPOSE,
					explanationText: i18n.t("sidebar.explanationTexts.compose"),
					icon: renderSidebarIcon(SidebarIcons.compose),
					feature: FeaturesCodes.SEQUENCER,
					type: "icon",
				},
				{
					title: i18n.t("sidebar.inbox"),
					path: ROUTE_CONFIG.MESSAGE_CENTER_INBOX,
					explanationText: i18n.t("sidebar.explanationTexts.inbox"),
					icon: renderSidebarIcon(SidebarIcons.inbox),
					feature: FeaturesCodes.SEQUENCER,
					type: "icon",
				},
				{
					title: i18n.t("sidebar.outbox"),
					path: ROUTE_CONFIG.MESSAGE_CENTER_OUTBOX,
					explanationText: i18n.t("sidebar.explanationTexts.outbox"),
					icon: renderSidebarIcon(SidebarIcons.outbox),
					feature: FeaturesCodes.SEQUENCER,
					type: "icon",
				},
			],
			signalsLeads: [
				{
					title: i18n.t("sidebar.listBuilder"),
					explanationText: i18n.t("sidebar.explanationTexts.listBuilder"),
					path: ROUTE_CONFIG.LISTS,
					icon: renderSidebarIcon(SidebarIcons.leadLists),
					type: "icon",
				},
				{
					title: i18n.t("sidebar.campaigns"),
					explanationText: i18n.t("sidebar.explanationTexts.campaigns"),
					path: ROUTE_CONFIG.CAMPAIGNS,
					icon: renderSidebarIcon(SidebarIcons.campaigns),
					feature: FeaturesCodes.CAMPAIGNS_OVERVIEW,
					type: "icon",
				},
				{
					title: i18n.t("sidebar.readyToSend"),
					path: ROUTE_CONFIG.MESSAGE_CENTER,
					explanationText: i18n.t("sidebar.explanationTexts.readyToSend"),
					icon: renderSidebarIcon(SidebarIcons.reviewLeads),
					feature: FeaturesCodes.SEQUENCER,
					type: "icon",
				},
				{
					title: i18n.t("sidebar.feed"),
					explanationText: i18n.t("sidebar.explanationTexts.feed"),
					path: ROUTE_CONFIG.FEED,
					icon: renderSidebarIcon(SidebarIcons.feed),
					permission: PermissionRoles.TRACKER,
					type: "icon",
				},
				{
					id: "contacts",
					title: i18n.t("sidebar.contacts"),
					icon: renderSidebarIcon(SidebarIcons.contacts),
					feature: FeaturesCodes.CONTACT,
					permission: PermissionRoles.CONTACT,
					type: "icon",
					subItems: [
						{
							title: i18n.t("sidebar.contacts"),
							explanationText: i18n.t("sidebar.explanationTexts.contacts"),
							path: ROUTE_CONFIG.CONTACTS,
							// `/app/contacts` must not stay lit on `/app/contacts/companies`.
							strictPath: true,
							icon: renderSidebarIcon(SidebarIcons.contacts),
							type: "icon",
						},
						{
							title: i18n.t("sidebar.contactsCompanies"),
							explanationText: i18n.t("sidebar.explanationTexts.contactsCompanies"),
							path: ROUTE_CONFIG.CONTACTS_COMPANIES,
							icon: renderSidebarIcon(SidebarIcons.companies),
							type: "icon",
						},
					],
				},
			],
			configurations: [
				{
					title: i18n.t("sidebar.leadScoring"),
					path: ROUTE_CONFIG.LEAD_SCORING,
					explanationText: i18n.t("sidebar.explanationTexts.leadScoring"),
					icon: renderSidebarIcon(SidebarIcons.leadScoring),
					type: "icon",
					feature: FeaturesCodes.LEAD_SCORING,
				},
				{
					title: i18n.t("sidebar.signals"),
					path: ROUTE_CONFIG.SIGNALS,
					explanationText: i18n.t("sidebar.explanationTexts.signals"),
					icon: renderSidebarIcon(SidebarIcons.signalScoring),
					type: "icon",
					feature: FeaturesCodes.TRACKER_AS_LIST,
				},
				{
					title: i18n.t("sidebar.knowledgeDb"),
					path: ROUTE_CONFIG.KNOWLEDGE_DB,
					explanationText: i18n.t("sidebar.explanationTexts.knowledgeDb"),
					icon: renderSidebarIcon(SidebarIcons.knowledgeDb),
					type: "icon",
					feature: FeaturesCodes.KNOWLEDGE_DB,
					dataOnboarding: "knowledge-db-nav",
				},
				{
					title: i18n.t("sidebar.copywriting"),
					path: ROUTE_CONFIG.COPYWRITING,
					explanationText: i18n.t("sidebar.explanationTexts.copywriting"),
					icon: renderSidebarIcon(SidebarIcons.copywriting),
					type: "icon",
					feature: FeaturesCodes.COPYWRITING_SETTINGS,
					permission: PermissionRoles.COPYWRITING_SETTINGS,
				},
				{
					title: i18n.t("sidebar.templates"),
					path: ROUTE_CONFIG.TEMPLATES,
					explanationText: i18n.t("sidebar.explanationTexts.templates"),
					icon: renderSidebarIcon(SidebarIcons.templates),
					type: "icon",
					feature: FeaturesCodes.TEMPLATES,
				},
				{
					id: "doNotContact",
					title: i18n.t("sidebar.doNotContact"),
					icon: renderSidebarIcon(SidebarIcons.doNotContact),
					feature: FeaturesCodes.CONTACT,
					permission: PermissionRoles.CONTACT,
					type: "icon",
					subItems: [
						{
							title: i18n.t("sidebar.competitors"),
							explanationText: i18n.t("sidebar.explanationTexts.competitors"),
							path: ROUTE_CONFIG.DO_NOT_CONTACT_COMPETITORS,
							icon: renderSidebarIcon(SidebarIcons.competitors),
							type: "icon",
						},
						{
							title: i18n.t("sidebar.blacklist"),
							explanationText: i18n.t("sidebar.explanationTexts.blacklist"),
							path: ROUTE_CONFIG.DO_NOT_CONTACT_BLACKLIST,
							icon: renderSidebarIcon(SidebarIcons.blacklist),
							type: "icon",
						},
					],
				},
				{
					title: i18n.t("sidebar.analytics"),
					path: ROUTE_CONFIG.ANALYTICS,
					explanationText: i18n.t("sidebar.explanationTexts.analytics"),
					icon: renderSidebarIcon(SidebarIcons.analytics),
					type: "icon",
					feature: FeaturesCodes.LEAD_SCORING,
				},
				{
					title: i18n.t("sidebar.integrations"),
					path: ROUTE_CONFIG.INTEGRATIONS,
					explanationText: i18n.t("sidebar.explanationTexts.integrations"),
					icon: renderSidebarIcon(SidebarIcons.integrations),
					type: "icon",
					permission: PermissionRoles.COMPANY,
				},
			],
			help: [
				{
					title: i18n.t("sidebar.support"),
					path: ROUTE_CONFIG.SUPPORT,
					icon: renderSidebarIcon(SidebarIcons.supportDashboard),
					feature: FeaturesCodes.SUPPORT_TICKETS,
					type: "icon",
				},
			],
			lower: [
				{
					title: i18n.t("sidebar.customEvent"),
					path: ROUTE_CONFIG.CUSTOM_EVENTS,
					icon: renderSidebarIcon(SidebarIcons.websites),
					permission: PermissionRoles.GLOBAL_ADMIN,
					type: "icon",
					companyIds: [1],
				},
				{
					title: i18n.t("sidebar.globalBlacklist"),
					path: ROUTE_CONFIG.BLACKLIST_GLOBAL,
					icon: renderSidebarIcon(SidebarIcons.globalBlacklist),
					permission: PermissionRoles.GLOBAL_ADMIN,
					type: "icon",
					companyIds: [1],
				},
				{
					title: "Feature Management",
					path: ROUTE_CONFIG.FEATURE_MANAGEMENT,
					icon: renderSidebarIcon(SidebarIcons.featureManagement),
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
					companyIds: [1],
				},
				{
					title: i18n.t("sidebar.supportDashboard"),
					path: ROUTE_CONFIG.SUPPORT_DASHBOARD,
					icon: renderSidebarIcon(SidebarIcons.supportDashboard),
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
					companyIds: [1],
				},
				{
					title: i18n.t("sidebar.featureUsage"),
					path: ROUTE_CONFIG.FEATURE_USAGE,
					icon: renderSidebarIcon(SidebarIcons.supportDashboard),
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
					companyIds: [1],
				},
				{
					title: i18n.t("support.staff.title"),
					path: ROUTE_CONFIG.TICKETS,
					icon: renderSidebarIcon(SidebarIcons.tickets),
					type: "icon",
					// Staff = any GLOBAL_ADMIN, at any company (PREC-3934 decision 1) — no companyIds.
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.adminFixes"),
					path: ROUTE_CONFIG.ADMIN_FIXES,
					icon: renderSidebarIcon(SidebarIcons.supportDashboard),
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
					companyIds: [1],
				},
				{
					title: i18n.t("sidebar.apiHealth"),
					path: ROUTE_CONFIG.API_HEALTH,
					icon: renderSidebarIcon(SidebarIcons.supportDashboard),
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
					companyIds: [1],
				},
				{
					title: i18n.t("sidebar.helpConversations"),
					path: ROUTE_CONFIG.HELP_CENTER_ADMIN,
					icon: renderSidebarIcon(SidebarIcons.helpArticles),
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
					companyIds: [1],
				},
				{
					title: i18n.t("sidebar.accountSettings"),
					path: ROUTE_CONFIG.COMPANY,
					explanationText: i18n.t("sidebar.explanationTexts.accountSettings"),
					icon: renderSidebarIcon(SidebarIcons.companySettings),
					type: "icon",
				},
				{
					title: i18n.t("sidebar.companies"),
					path: ROUTE_CONFIG.COMPANIES,
					icon: renderSidebarIcon(SidebarIcons.companies),
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
					companyIds: [1],
				},
			],
		},
	};
};
